import React, { useMemo } from "react";

const ITEM_META = {
  passkey: { icon: "ri-fingerprint-line", short: "Passkey" },
  google: { icon: "ri-shield-keyhole-line", short: "Google Auth" },
  email: { icon: "ri-mail-line", short: "Email" },
  sms: { icon: "ri-smartphone-line", short: "SMS" },
  antiPhishing: { icon: "ri-safe-2-line", short: "Anti-Phishing" },
  login2step: { icon: "ri-lock-password-line", short: "Login 2-Step" },
};

function getSubtitle(active, total) {
  if (total === 0) return "Review and enable core security options below.";
  if (active === total) return "Great work — all core safeguards below are turned on.";
  if (active === 0) return "Enable verification methods below to secure your account.";
  const remaining = total - active;
  return `${remaining} more core safeguard${remaining > 1 ? "s" : ""} can be enabled below. Fund password and withdrawal settings are separate.`;
}

/**
 * Premium account protection summary for the security settings page.
 * Inactive cards navigate to the matching setup flow via `onInactiveClick`.
 */
export default function SecurityLevelOverview({ items = [], onInactiveClick }) {
  const total = items.length;
  const active = useMemo(() => items.filter((it) => it.enabled).length, [items]);
  const progress = total > 0 ? active / total : 0;
  const percent = Math.round(progress * 100);

  return (
    <section className="tf-sec-vault" aria-label="Account protection summary">
      <div className="tf-sec-vault__panel">
        <div className="tf-sec-vault__accent" aria-hidden />

        <div className="tf-sec-vault__header">
          <div
            className="tf-sec-vault__orb"
            style={{ "--vault-progress": `${percent}%` }}
            role="img"
            aria-label={`${percent} percent of core safeguards active`}
          >
            <div className="tf-sec-vault__orb-inner">
              <i className="ri-shield-check-line" aria-hidden />
              <div className="tf-sec-vault__orb-count">
                <span className="tf-sec-vault__orb-score">{active}</span>
                <span className="tf-sec-vault__orb-total">/{total}</span>
              </div>
            </div>
          </div>

          <div className="tf-sec-vault__intro">
            <p className="tf-sec-vault__kicker">Core safeguards</p>
            <h2 className="tf-sec-vault__headline">
              {active} of {total} core safeguards enabled
            </h2>
            <p className="tf-sec-vault__subtitle">{getSubtitle(active, total)}</p>
          </div>

          <div className="tf-sec-vault__summary">
            <div
              className="tf-sec-vault__coverage"
              style={{ "--vault-coverage": `${percent}%` }}
            >
              <div className="tf-sec-vault__coverage-meta">
                <span className="tf-sec-vault__coverage-value">{percent}%</span>
                <span className="tf-sec-vault__coverage-label">Core coverage</span>
              </div>
              <div className="tf-sec-vault__coverage-track" aria-hidden>
                <div className="tf-sec-vault__coverage-fill" />
              </div>
            </div>
          </div>
        </div>

        <div className="tf-sec-vault__grid">
          {items.map((item) => {
            const meta = ITEM_META[item.id] || { icon: "ri-shield-line", short: item.label };
            const isClickable = !item.enabled && typeof onInactiveClick === "function";
            const label = item.enabled
              ? (item.enabledLabel || "Active")
              : (item.disabledLabel || "Setup needed");

            const content = (
              <>
                <span className="tf-sec-vault__card-icon" aria-hidden>
                  <i className={meta.icon} />
                </span>
                <div className="tf-sec-vault__card-body">
                  <h3 className="tf-sec-vault__card-title">{meta.short}</h3>
                  <span className={`tf-sec-vault__card-badge${item.enabled ? " is-live" : ""}`}>
                    {label}
                  </span>
                </div>
                <span className={`tf-sec-vault__card-check${item.enabled ? " is-live" : ""}`} aria-hidden>
                  <i className={item.enabled ? "ri-check-line" : "ri-arrow-right-s-line"} />
                </span>
              </>
            );

            if (isClickable) {
              return (
                <button
                  key={item.id}
                  type="button"
                  className="tf-sec-vault__card tf-sec-vault__card--action"
                  onClick={() => onInactiveClick(item.id)}
                  aria-label={`Set up ${meta.short}`}
                >
                  {content}
                </button>
              );
            }

            return (
              <article
                key={item.id}
                className={`tf-sec-vault__card${item.enabled ? " is-live" : ""}`}
              >
                {content}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
